<script setup>
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-385 + RED-361: what changed between two checklist snapshots
 * (`ChecklistVersionWriter::changes()`), one line per Üldjuhis section, item
 * field or question field. Used by the version history and by the upload
 * dialog (`Laadi üles`), so both say a change the same way. A long text opens
 * on demand as old (struck through) and new.
 */
defineProps({
  changes: { type: Array, required: true },
})

const { t } = useI18n()

const LONG_TEXT = 80
const open = reactive({})

const changeKey = change => `${change.scope}-${change.ref}-${change.field}`

const changeLabel = change => {
  const prefix = 'messages.admin.report_qc.checklist_history'
  if (change.scope === 'frame') {
    return t(`${prefix}.section_${change.ref}`)
  }
  const owner = change.scope === 'item' ? t(`${prefix}.item`, { n: change.ref }) : t(`${prefix}.query`, { key: change.ref })
  if (change.field === null) {
    return `${owner}, ${t(`${prefix}.${change.scope}_added`)}`
  }
  const field = { title: 'title', body: 'body', active: 'active', block: 'block', prompt: 'prompt', covers_items: 'covers', uses_standards_index: 'index', sort_order: 'sort' }[change.field]
  return `${owner}, ${t(`${prefix}.field_${field}`)}`
}

const formatValue = value => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  if (typeof value === 'boolean') {
    return t(`messages.admin.report_qc.checklist_history.${value ? 'yes' : 'no'}`)
  }
  if (Array.isArray(value)) {
    return value.join(', ')
  }
  return String(value)
}

const isLong = change => [change.old, change.new].some(value => typeof value === 'string' && value.length > LONG_TEXT)
</script>

<template>
  <ul class="flex flex-col gap-1 text-sm">
    <li v-for="change in changes" :key="changeKey(change)" data-testid="checklist-change">
      <template v-if="isLong(change)">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-gray-600">{{ changeLabel(change) }}</span>
          <Button
            :label="open[changeKey(change)] ? t('messages.admin.report_qc.checklist_history.hide_text') : t('messages.admin.report_qc.checklist_history.show_text')"
            link
            size="small"
            class="p-0!"
            @click="open[changeKey(change)] = !open[changeKey(change)]"
          />
        </div>
        <div v-if="open[changeKey(change)]" class="mt-1 flex flex-col gap-1">
          <pre class="rounded bg-red-50 px-2 py-1 font-mono text-xs whitespace-pre-wrap text-gray-600 line-through">{{ formatValue(change.old) }}</pre>
          <pre class="rounded bg-green-50 px-2 py-1 font-mono text-xs whitespace-pre-wrap">{{ formatValue(change.new) }}</pre>
        </div>
      </template>
      <template v-else>
        <span class="text-gray-600">{{ changeLabel(change) }}:</span>
        <template v-if="change.field !== null || change.scope === 'frame'">
          <span class="ml-1 text-gray-500 line-through">{{ formatValue(change.old) }}</span>
          <i class="pi pi-arrow-right mx-1 text-xs text-gray-400" aria-hidden="true" />
        </template>
        <span class="font-medium">{{ formatValue(change.new) }}</span>
      </template>
    </li>
  </ul>
</template>
